import type { MerchantPermissionId } from "@/modules/roles/domain/contracts/merchant-permissions";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";

/** ระดับการยืนยันร้าน — ใช้จำกัดฟีเจอร์ตาม business policy */
export type ShopVerifiedLevel = "none" | "basic" | "verified";

/** สถานะร้าน (เปิดใช้ / ระงับ / ยังไม่ผ่านขั้นตอน) */
export type ShopStatus = "active" | "suspended" | "unverified";

const LEVEL_ORDER: Record<ShopVerifiedLevel, number> = {
  none: 0,
  basic: 1,
  verified: 2,
};

/** ระดับต่ำสุดที่อนุญาตต่อ permission — ไม่ในรายการ = "basic" */
const MIN_LEVEL_BY_PERMISSION: Partial<
  Record<MerchantPermissionId, ShopVerifiedLevel>
> = {
  [MerchantPermissions.catalog.read]: "basic",
  [MerchantPermissions.catalog.create]: "basic",
  [MerchantPermissions.catalog.update]: "basic",
  [MerchantPermissions.catalog.delete]: "basic",
  [MerchantPermissions.inventory.read]: "basic",
  [MerchantPermissions.inventory.adjust]: "basic",
  [MerchantPermissions.inventory.receive]: "basic",
  [MerchantPermissions.inventory.transfer]: "basic",
  [MerchantPermissions.pos.read]: "basic",
  [MerchantPermissions.pos.sell]: "basic",
  [MerchantPermissions.pos.refund]: "basic",
  [MerchantPermissions.pos.void]: "basic",
  [MerchantPermissions.settlement.read]: "basic",
  [MerchantPermissions.settlement.requestPayout]: "verified",
  [MerchantPermissions.shop_staff.read]: "none",
  [MerchantPermissions.shop_staff.create]: "basic",
  [MerchantPermissions.shop_staff.update]: "basic",
  [MerchantPermissions.shop_staff.delete]: "basic",
  [MerchantPermissions.shop_roles.read]: "none",
  [MerchantPermissions.shop_roles.create]: "basic",
  [MerchantPermissions.shop_roles.update]: "basic",
  [MerchantPermissions.shop_roles.delete]: "basic",
  [MerchantPermissions.shifts.read]: "none",
  [MerchantPermissions.shifts.manage]: "basic",
  [MerchantPermissions.shifts.clock]: "basic",
  [MerchantPermissions.claims.read]: "basic",
  [MerchantPermissions.claims.create]: "basic",
  [MerchantPermissions.claims.manage]: "basic",
  [MerchantPermissions.promotions.read]: "basic",
  [MerchantPermissions.promotions.manage]: "basic",
  [MerchantPermissions.marketplace.read]: "basic",
  [MerchantPermissions.marketplace.publish]: "basic",
  [MerchantPermissions.workshop.read]: "basic",
  [MerchantPermissions.workshop.create]: "basic",
  [MerchantPermissions.workshop.sell]: "basic",
  [MerchantPermissions.workshop.manage]: "basic",
};

const DEFAULT_MIN_LEVEL: ShopVerifiedLevel = "basic";

/**
 * ตรวจว่า level ปัจจุบันเพียงพอต่อระดับที่ต้องการหรือไม่
 * null/undefined ถือเป็น "none"
 */
export function isLevelSufficient(
  current: ShopVerifiedLevel | null | undefined,
  minRequired: ShopVerifiedLevel,
): boolean {
  const level = current ?? "none";
  return LEVEL_ORDER[level] >= LEVEL_ORDER[minRequired];
}

/** คืนระดับต่ำสุดที่ต้องมีเพื่อใช้ permission นี้ */
export function getMinVerifiedLevelForPermission(
  permissionId: MerchantPermissionId,
): ShopVerifiedLevel {
  return MIN_LEVEL_BY_PERMISSION[permissionId] ?? DEFAULT_MIN_LEVEL;
}

/**
 * นโยบายจำกัดฟีเจอร์ตาม verifiedLevel (สรุปให้ UI/เอกสารใช้)
 * - none: ดู dashboard, ตั้งค่าพื้นฐาน (roles/staff อ่านได้) — ยังขาย/ settlement ไม่ได้
 * - basic: ใช้ catalog, inventory, POS, settlement อ่านได้, จัดการ staff/roles ได้
 * - verified: ใช้ครบ รวม settlement ขอ payout
 */
export const SHOP_VERIFICATION_POLICY = {
  none: {
    label: "ຍັງບໍ່ຢືນຢັນ",
    description:
      "ເຂົ້າເຖິງແຜງຄວບຄຸມ ແລະ ຕັ້ງຄ່າພະນັກງານ/ບົດບາດໄດ້ — ຕ້ອງຢືນຢັນລາຍການເພື່ອຂາຍ ຫຼື ຊຳລະ",
  },
  basic: {
    label: "ຢືນຢັນເບື້ອງຕົ້ນ",
    description:
      "ຂາຍສິນຄ້າ, ສາງ, POS ແລະ ເບິ່ງລາຍງານຊຳລະໄດ້ — ຂໍ payout ຕ້ອງຢືນຢັນລະດັບສູງ",
  },
  verified: {
    label: "ຢືນຢັນຄົບ",
    description: "ໃຊ້ທຸກຟີເຈີໄດ້ຄົບຖ້ວນ ລວມການຂໍຊຳລະ",
  },
} as const;
